import { useState, useEffect } from "react";
import classes from "./AddingVideoFotoModal.module.css";
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import ErrorMessage from "../../components/StudioErstellen/ErrorMessage";

export const AddingVideoFotoModal = ({ open, onBackdropClick, onModeSelect, onLinkSubmit }) => {
    const [mode, setMode] = useState(null); // null, 'photo', 'video', 'link'
    const [linkUrl, setLinkUrl] = useState('');
    const [linkError, setLinkError] = useState('');

    const handleClose = () => {
        setMode(null);
        setLinkUrl('');
        setLinkError('');
        onBackdropClick();
    };

    const handleModeSelect = (selectedMode) => {
        if (selectedMode === 'link') {
            // For link, stay in modal to enter URL
            setMode('link');
        } else {
            // For photo/video, close modal and trigger file input in parent
            handleClose();
            onModeSelect(selectedMode);
        }
    };

    const handleLinkSubmit = () => {
        setLinkError('');
        
        if (!linkUrl.trim()) {
            setLinkError('Bitte gib einen Link ein.');
            return;
        }

        try {
            const url = new URL(linkUrl);
            const hostname = url.hostname.toLowerCase();

            // Check if it's YouTube or Vimeo
            const isYouTube = hostname.includes('youtube.com') || hostname.includes('youtu.be');
            const isVimeo = hostname.includes('vimeo.com');
            
            if (!isYouTube && !isVimeo) {
                setLinkError('Bitte verwende einen YouTube oder Vimeo Link.');
                return;
            }

            // Generate thumbnail URL for YouTube/Vimeo
            let thumbnailUrl = '';
            if (isYouTube) {
                const videoId = hostname.includes('youtu.be') 
                    ? url.pathname.slice(1)
                    : url.searchParams.get('v');
                if (videoId) {
                    thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                }
            } else if (isVimeo) {
                const videoId = url.pathname.split('/').filter(p => p).pop();
                if (videoId) {
                    // Vimeo requires API call for thumbnail, but we can try this
                    thumbnailUrl = `https://vumbnail.com/${videoId}.jpg`;
                }
            }

            const linkItem = {
                type: 'link',
                url: linkUrl.trim(),
                thumbnailUrl: thumbnailUrl,
                size: 0 // Links don't count towards file size
            };

            onLinkSubmit(linkItem);
            handleClose();
        } catch (error) {
            setLinkError('Ungültiger Link. Bitte verwende einen vollständigen YouTube oder Vimeo URL.');
        }
    };

    return (
        <SmallModal fromVideoFoto={true} open={open} onBackdropClick={handleClose}>
            <div className={classes.addingVideoFotoModal}>
                {!mode ? (
                    <div className={classes.optionsContainer}>
                        <header className={classes.header}>
                            <svg onClick={handleClose} className={classes.closeButton} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                            <h3 className="titleSemibold2">Fotos oder Videos hochladen</h3>
                        </header>
                        <div className={classes.optionsList}>
                            <div className={classes.videoFotoInfoBox}>
                                <span>💡</span>
                                <p>Fotos & Videos bis 100MB.<br />
                                Größere Videos per YouTube oder Vimeo.</p>
                            </div>
                            <button 
                                className={classes.optionButton}
                                onClick={() => handleModeSelect('photo')}
                            > 
                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none">
                                    <path d="M17.9246 2.46839H15.3988C15.3127 2.46839 15.2266 2.41099 15.1835 2.33923L14.5233 0.947175C14.2507 0.373129 13.6623 0 13.0165 0H7.47694C6.83114 0 6.22839 0.373129 5.95572 0.961526L5.32427 2.33923C5.32427 2.33923 5.19511 2.46839 5.109 2.46839H2.5832C1.16244 2.46839 0 3.63084 0 5.0516V14.4372C0 15.8724 1.16244 17.0204 2.5832 17.0204H17.9246C19.3453 17.0204 20.5078 15.8724 20.5078 14.4372V5.0516C20.5078 3.63084 19.3453 2.46839 17.9246 2.46839ZM19.0727 14.4372C19.0727 15.0687 18.556 15.5853 17.9246 15.5853H2.5832C1.95175 15.5853 1.43511 15.0687 1.43511 14.4372V5.0516C1.43511 4.42015 1.95175 3.90351 2.5832 3.90351H5.109C5.7548 3.90351 6.3432 3.53038 6.63022 2.94198L7.26167 1.56427C7.30473 1.47817 7.39083 1.43511 7.47694 1.43511H13.0165C13.1026 1.43511 13.1887 1.47817 13.2317 1.56427L13.8919 2.95633C14.1646 3.53038 14.753 3.90351 15.3988 3.90351H17.9246C18.556 3.90351 19.0727 4.42015 19.0727 5.0516V14.4372Z" fill="black"/>
                                    <path d="M10.2468 14.5236C7.39087 14.5236 5.06599 12.1987 5.06599 9.34281C5.06599 6.48694 7.39087 4.1764 10.2468 4.1764C13.1026 4.1764 15.4132 6.50129 15.4132 9.34281C15.4132 12.1843 13.0883 14.5236 10.2468 14.5236ZM10.2468 5.61152C8.18019 5.61152 6.50111 7.2906 6.50111 9.34281C6.50111 11.395 8.18019 13.0885 10.2468 13.0885C12.3133 13.0885 13.978 11.4094 13.978 9.34281C13.978 7.27625 12.299 5.61152 10.2468 5.61152Z" fill="black"/>
                                    <path d="M18.0536 5.95617C18.0536 6.61632 17.5226 7.14731 16.8625 7.14731C16.2023 7.14731 15.6713 6.61632 15.6713 5.95617C15.6713 5.29602 16.2023 4.76502 16.8625 4.76502C17.5226 4.76502 18.0536 5.31037 18.0536 5.95617Z" fill="black"/>
                                </svg>
                                <span className="mediumLarge">
                                Fotobibliothek
                                </span>
                            </button>
                            <button 
                                className={classes.optionButton}
                                onClick={() => handleModeSelect('video')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none">
                                    <path d="M18.5417 2.19572H8.45282L6.97465 0.416183C6.75938 0.157862 6.41496 0 6.08488 0H2.19572C0.990228 0 0 0.975877 0 2.18137V14.8821C0 16.0876 0.990228 17.0778 2.19572 17.0778H18.5417C19.7472 17.0778 20.7374 16.0876 20.7374 14.8821V4.39145C20.7374 3.18595 19.7472 2.19572 18.5417 2.19572ZM1.43511 2.18137C1.43511 1.76519 1.77954 1.43511 2.19572 1.43511H5.95572L7.43389 3.21465C7.6348 3.47297 7.99358 3.63084 8.30931 3.63084H18.5417C18.9578 3.63084 19.3023 3.97526 19.3023 4.39145V5.42473H1.43511V2.18137ZM19.3023 14.8821C19.3023 15.2983 18.9578 15.6427 18.5417 15.6427H2.19572C1.77954 15.6427 1.43511 15.2983 1.43511 14.8821V6.85984H19.3023V14.8821Z" fill="black"/>
                                </svg>
                                <span className="mediumLarge">Dateien hinzufügen</span>
                            </button>
                            <button 
                                className={classes.optionButton}
                                onClick={() => handleModeSelect('link')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                                    <path d="M4.23358 18.4191C3.15725 18.4191 2.06656 18.0029 1.2342 17.1849C0.444885 16.3812 0 15.3192 0 14.1855C0 13.0517 0.444885 12.0041 1.24855 11.2148L4.31969 8.14366C4.60671 7.85664 5.0516 7.85664 5.33862 8.14366C5.62564 8.43068 5.62564 8.87557 5.33862 9.16259L2.26748 12.2337C1.73649 12.7647 1.44946 13.4536 1.44946 14.1998C1.44946 14.9461 1.73649 15.6493 2.26748 16.1946C3.35817 17.271 5.13771 17.271 6.21404 16.1946L9.84488 12.5638C10.1175 12.2911 10.3328 11.9611 10.4763 11.6166C10.6055 11.2866 10.6629 10.9278 10.6629 10.5977C10.6629 9.83709 10.3759 9.14824 9.84488 8.6316C9.5148 8.41633 9.42869 7.9571 9.64396 7.62702C9.85923 7.29694 10.3041 7.19649 10.6342 7.4261C10.7203 7.48351 10.7633 7.52656 10.8495 7.61267C11.6531 8.40198 12.098 9.46396 12.098 10.6121C12.098 11.1287 11.9975 11.6597 11.811 12.162C11.5957 12.7073 11.28 13.1953 10.8638 13.6114L7.23297 17.2423C6.41496 18.0603 5.32427 18.4765 4.24794 18.4765L4.23358 18.4191Z" fill="black"/>
                                    <path d="M8.06535 10.8853C7.86443 10.8853 7.67787 10.7992 7.53436 10.6414C6.85986 9.88075 6.47237 8.87617 6.47237 7.84289C6.47237 7.3406 6.57283 6.79526 6.7594 6.29297C6.97466 5.74763 7.30474 5.25969 7.70657 4.84351L11.3374 1.22702C12.9734 -0.409007 15.6571 -0.409007 17.3218 1.22702C18.1255 2.03069 18.5704 3.10702 18.5704 4.24076C18.5704 5.3745 18.1255 6.42213 17.3218 7.21144L14.2507 10.2826C13.9637 10.5696 13.5188 10.5696 13.2318 10.2826C12.9447 9.99556 12.9447 9.55068 13.2318 9.26366L16.3029 6.19251C16.8339 5.66152 17.1209 4.97267 17.1209 4.22641C17.1209 3.48015 16.8339 2.77694 16.3029 2.2316C15.2122 1.15527 13.4327 1.15527 12.3563 2.2316L8.7255 5.86244C8.45283 6.13511 8.23756 6.46518 8.09405 6.80961C7.96489 7.13969 7.90749 7.49847 7.90749 7.82854C7.90749 8.5174 8.15146 9.17755 8.59634 9.67984C8.85466 9.98121 8.84031 10.4261 8.53894 10.6988C8.39543 10.8136 8.23756 10.8853 8.06535 10.8853Z" fill="black"/>
                                </svg>
                                <span className="mediumLarge">Youtube/Vimeo Video hinzufügen</span>
                            </button>
                        </div>
                    </div>
                ) : mode === 'link' ? (
                    <div className={classes.linkContainer}>
                        <header className={classes.header}>
                            <h3 className="titleSemibold2">Link hinzufügen</h3>
                            <svg onClick={handleClose} className={classes.closeButton} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                            </svg>
                        </header>
                        <p className={classes.linkInfoText}>
                        💡 Funktioniert für Youtube und Vimeo.
                        </p>
                        <input
                            type="url"
                            className={`${classes.linkInput} regularMiddle ${linkError ? classes.inputError : ''}`}
                            placeholder="Link einfügen"
                            value={linkUrl}
                            onChange={(e) => {
                                setLinkUrl(e.target.value);
                                if (linkError) setLinkError('');
                            }}
                        />
                        {linkError && (
                            <ErrorMessage>{linkError}</ErrorMessage>
                        )}
                        <div className={classes.linkButtons}>
                            {/* <button 
                                className={classes.backButton}
                                onClick={() => {
                                    setMode(null);
                                    setLinkUrl('');
                                    setLinkError('');
                                }}
                            >
                                Zurück
                            </button> */}
                            <button 
                                className={`${classes.submitButton} ${!linkUrl.trim() ? classes.disabledButton : ''}`}
                                onClick={handleLinkSubmit}
                                disabled={!linkUrl.trim()}
                            >
                                Video hinzufügen
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </SmallModal>
    );
};

export default AddingVideoFotoModal;
